<script>
import Bar from '../components/widgets/bar.vue'
import { emptyCenterTextPlugin, subGroupPlugin, yearLabelPlugin } from "@/utils/chartjs/graphPlugins";

export default {
  name: "monthly-harvest-size-distribution",
  components: {
    Bar
  },
  props: {
    data: {
      type: Array, default: () => [],
    },
    zones: {
      type: Array, default() {
        return [];
      }
    },
    dates: {
      type: Array, default() {
        return [this.$moment().startOf('years').format('YYYY-MM-DD'), this.$moment().format('YYYY-MM-DD')];
      }
    }
  },
  data() {
    return {
      barPercentage: 0.6,
      quantityCategories: ['Small', 'Medium', 'Large'],
    }
  },
  computed: {
    monthlyMoments() {
      let startDate = this.$moment(this.dates[0]).startOf('month')
      let endDate = this.$moment(this.dates[1]).startOf('month')

      let months = []

      for (let currMonth = startDate; currMonth.isSameOrBefore(endDate); currMonth.add(1, 'months')) {
        months.push(currMonth.clone())
      }

      return months
    },
    chartLabels() {
      return this.monthlyMoments.map(moment => moment.format('YYYY-MMM'));
    },
    chartYears() {
      let years = [...new Set(this.chartLabels.map(label => label.split('-')[0]))]
      return years.map((year) => ({
        year: year,
        startIndex: this.chartLabels.findIndex(label => label.split('-')[0] == year),
        endIndex: this.chartLabels.findLastIndex(label => label.split('-')[0] == year),
      }));
    },
    chartDataset() {
      let dataset = []
      this.zones.forEach(zone => {
        this.quantityCategories.forEach(category => {
          let backgroundColor = this.barBackgroundColorBySizeCategory(category)

          dataset.push({
            backgroundColor: backgroundColor,
            borderColor: backgroundColor,
            borderWidth: 3,
            data: this.chartDataByZoneAndSizeCategory(zone, category),
            label: this.labelByCategory(category),
            size_category: category,
            stack: this.stackByZone(zone),
            zone: `${this.$t(`zones.singular`)} ${zone}`,
            zone_id: zone,
          })
        })
      })
      return dataset
    },
    chartData() {
      return {
        labels: this.chartLabels,
        years: this.chartYears,
        datasets: this.chartDataset,
      }
    },
    chartOptions() {
      let vm = this

      return {
        emptyState: {
          text: this.$t('menuitems.charts.no_data')
        },

        legend: {
          position: 'top',
          labels: {
            generateLabels: function (chart) {
              const datasets = chart.data.datasets;
              const uniqueLabels = [...new Set(datasets.map(ds => ds.label))];
              const colors = [...new Set(datasets.map(ds => ds.backgroundColor))];

              return uniqueLabels.map((label, i) => {
                return {
                  text: label,
                  fillStyle: colors[i % colors.length],
                  hidden: false,
                  lineCap: 'butt',
                  lineDash: [],
                  lineDashOffset: 0,
                  lineJoin: 'miter',
                  lineWidth: 1,
                  strokeStyle: colors[i % colors.length],
                  pointStyle: 'circle',
                  index: i
                };
              });
            },
            onClick: (e) => e.stopPropagation()
          }
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{
            id: 'month-axis',
            afterFit: function (scaleInstance) {
              scaleInstance.height += 50
            },
            barPercentage: this.barPercentage,
            gridLines: {
              display: false
            },
            stacked: true,
            ticks: {
              padding: 15,
              callback: function (value) {
                return value.split('-')[1];
              }
            }
          }],
          yAxes: [{
            id: 'y-axis-left',
            stacked: true,
            ticks: {
              beginAtZero: true,
              max: 100,
              stepSize: 20,
            },
            scaleLabel: {
              display: true,
              labelString: this.lodash.upperFirst(`${this.$t('percentage')} (%)`),
            }
          }]
        },
        tooltips: {
          enabled: false,
          mode: 'x',
          intersect: false,
          custom: function (tooltipModel) {
            var tooltipEl = document.getElementById('chartjs-tooltip');

            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.style.background = 'transparent';
              document.body.appendChild(tooltipEl);
            }

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            let dataIndex = tooltipModel.dataPoints[0].index;
            const chartData = this._data;
            let label = chartData.labels[dataIndex];
            let dataset = chartData.datasets[tooltipModel.dataPoints[0].datasetIndex];
            let tooltipTitleText = `${vm.lodash.upperFirst(vm.$t('zones.singular'))} ${dataset.zone_id}`
            let tooltipSubtitleText = `${vm.$moment(label, 'YYYY-MMM').format('MMMM YYYY')}`
            let smallDataset =  chartData.datasets.find(zone => zone.size_category.toLowerCase() === 'small' && zone.zone_id == dataset.zone_id)
            let mediumDataset =  chartData.datasets.find(zone => zone.size_category.toLowerCase() === 'medium' && zone.zone_id == dataset.zone_id)
            let largeDataset =  chartData.datasets.find(zone => zone.size_category.toLowerCase() === 'large' && zone.zone_id == dataset.zone_id)
            let smallPercentage = smallDataset.data[dataIndex]
            let mediumPercentage = mediumDataset.data[dataIndex]
            let largePercentage = largeDataset.data[dataIndex]

            tooltipEl.innerHTML = `
              <div class="tooltip-container">
                <div class="tooltip-header">
                  <div class="tooltip-title">${tooltipTitleText}</div>
                  <div class="tooltip-subtitle">${tooltipSubtitleText}</div>
                </div>
                <div class="tooltip-content">
                <div class="tooltip-row">
                  <div class="tooltip-label">${vm.lodash.upperFirst(vm.$t('sizes.size_categories.large'))}</div>
                    <div class="tooltip-value">${largePercentage} %</div>
                    <div class="tooltip-indicator" style="background-color: ${largeDataset.backgroundColor} !important;"></div>
                  </div>
                  <div class="tooltip-row">
                  <div class="tooltip-label">${vm.lodash.upperFirst(vm.$t('sizes.size_categories.medium'))}</div>
                    <div class="tooltip-value">${mediumPercentage} %</div>
                    <div class="tooltip-indicator" style="background-color: ${mediumDataset.backgroundColor} !important;"></div>
                  </div>
                <div class="tooltip-row">
                  <div class="tooltip-label">${vm.lodash.upperFirst(vm.$t('sizes.size_categories.small'))}</div>
                  <div class="tooltip-value">${smallPercentage} %</div>
                  <div class="tooltip-indicator" style="background-color: ${smallDataset.backgroundColor} !important;"></div>
                </div>
                </div>
              </div>
            `;

            var position = this._chart.canvas.getBoundingClientRect();
            var cursorX = tooltipModel.caretX;

            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.background = 'transparent';

            setTimeout(() => {
              var tooltipWidth = tooltipEl.offsetWidth;
              var tooltipHeight = tooltipEl.offsetHeight;

              var horizontalPosition;
              var transformValue;
              var verticalOffset = 10;

              var viewportWidth = window.innerWidth;
              var tooltipRight = position.left + cursorX + tooltipWidth + 20;
              var tooltipLeft = position.left + cursorX - tooltipWidth - 20;

              if (tooltipRight > viewportWidth && tooltipLeft > 0) {
                horizontalPosition = position.left + window.pageXOffset + cursorX - 20 + 'px';
                transformValue = 'translate(-100%, 0)';
              } else {
                horizontalPosition = position.left + window.pageXOffset + cursorX + 20 + 'px';
                transformValue = 'translate(0, 0)';
              }

              var viewportHeight = window.innerHeight;
              var tooltipBottom = position.top + tooltipModel.caretY + tooltipHeight + verticalOffset;

              tooltipEl.style.left = horizontalPosition;
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + verticalOffset + 'px';
              tooltipEl.style.transform = transformValue;

              if (tooltipBottom > viewportHeight) {
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipHeight - verticalOffset + 'px';
              }
            }, 0);
          }
        },
      }
    },
    chartPlugins() {
      return [
        subGroupPlugin,
        yearLabelPlugin,
        emptyCenterTextPlugin,
      ]
    },
  },
  methods: {
    barBackgroundColorBySizeCategory(sizeCategory) {
      if (sizeCategory == 'Small') {
        return '#BDBDBD'
      } else if (sizeCategory == 'Medium') {
        return '#FFA000'
      } else if (sizeCategory == 'Large') {
        return '#1B77DF'
      }
      return null
    },
    chartDataByZoneAndSizeCategory(zoneId, sizeCategory) {
      return this.monthlyMoments.map(moment => {
        let currData = this.data.find(({period, zone_id}) => period === moment.format('YYYY-MM') && zone_id === zoneId)
        if (!currData) return 0
        return currData.size_categories[sizeCategory.toLowerCase()].percentage ?? 0
      })
    },
    labelByCategory(category) {
      if (category == 'Small') {
        return `${category} (x ≥ 90)`
      } else if (category == 'Medium') {
        return `${category} (40 ≤ x < 90)`
      } else if (category == 'Large') {
        return `Big (10 ≤ x < 40)`
      }
      return null
    },
    stackByZone(zoneId) {
      if (zoneId == 1) {
        return 'I';
      } else if (zoneId == 2) {
        return 'II';
      } else if (zoneId == 3) {
        return 'III';
      } else {
        return null;
      }
    }
  }
}
</script>

<template>
  <div class="col-12">
    <b-card class="shadow-sm mb-4" no-body>
      <b-card-body>
        <h3 class="dashboard-card-header mb-4">Harvest Size Distribution</h3>
        <bar
            :key="`harvest-size-distribution-chart-${dates[0]}-${dates[1]}-${zones.join('-')}`"
            :data="chartData"
            :height="350"
            :options="chartOptions"
            :plugins="chartPlugins"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<style lang="scss" scoped>
.overview-card-title {
  color: #667085;
  font: 500 16px/24px Inter;
}

.overview-card-body {
  color: #1664D8;
  font: 800 20px/28px Inter;
}
</style>